import { useRef } from "react";
import { navigationFor } from "../navigation/navigation";

function roleLabel(user) {
  if (user?.title) return user.title;

  const role = String(user?.role || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");

  const labels = {
    OWNER: "Owner",
    SYSTEM_OWNER: "Owner",
    SYSTEMOWNER: "Owner",
    PARTNER: "Partner",
    DIRECTOR_OF_OPERATIONS: "Director of Operations",
    TRANSACTION_COORDINATOR: "Transaction Coordinator",
    ADMIN: "Administrator",
    AGENT: "Agent"
  };

  return labels[role] || "Team Member";
}

function initials(name) {
  return String(name || "Team Member")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join("");
}

export default function AppShell({ user, page, onNavigate, children }) {
  const sidebarRef = useRef(null);
  const nav = navigationFor(user?.role);
  const mobileNav = nav
    .filter(([id]) => ["home", "transactions", "daily-rhythm", "calendar", "library", "tasks"].includes(id))
    .slice(0, 5);

  function collapseSidebarAfterPointerLeave() {
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement && sidebarRef.current?.contains(activeElement)) {
      activeElement.blur();
    }
  }

  return (
    <div className="app-shell app-shell--sidebar">
      <aside
        ref={sidebarRef}
        className="app-sidebar"
        data-app-shell-sidebar
        aria-label="ROOTS application navigation"
        onMouseLeave={collapseSidebarAfterPointerLeave}
      >
        <button
          type="button"
          className="sidebar-brand"
          onClick={() => onNavigate("home")}
          aria-label="EYGS ROOTS home"
        >
          <span className="sidebar-mark" aria-hidden="true">R</span>
          <span className="sidebar-label sidebar-brand-name">EYGS ROOTS</span>
        </button>

        <nav className="sidebar-nav" aria-label="Primary navigation">
          {nav.map(([id, icon, label]) => (
            <button
              key={id}
              type="button"
              className={`sidebar-nav-item${page === id ? " active" : ""}`}
              onClick={() => onNavigate(id)}
              title={label}
              aria-current={page === id ? "page" : undefined}
            >
              <span className="sidebar-nav-icon" aria-hidden="true">{icon}</span>
              <span className="sidebar-label">{label}</span>
            </button>
          ))}
        </nav>

        <div className="sidebar-user">
          <span className="sidebar-user-mark" aria-hidden="true">{initials(user?.name)}</span>
          <span className="sidebar-label sidebar-user-copy">
            <strong>{user?.name || "Team Member"}</strong>
            <small>{roleLabel(user)}</small>
          </span>
        </div>
      </aside>

      <div className="app-content">
        <main className="app-main" data-app-shell-main>{children}</main>
      </div>

      <nav className="mobile-nav" data-app-shell-mobile-nav aria-label="Mobile navigation">
        {mobileNav.map(([id, icon, label]) => (
          <button key={id} type="button" className={page === id ? "active" : ""} onClick={() => onNavigate(id)}>
            <span aria-hidden="true">{icon}</span><small>{label}</small>
          </button>
        ))}
      </nav>
    </div>
  );
}
